import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, h1, btnPrimary } from "./ui";
import { Download, FileJson, FileSpreadsheet, Loader2, ShieldAlert, Clock } from "lucide-react";

const fmtWhen = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return null; }
};
const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

export default function Backup() {
  const [busy, setBusy] = useState("");
  const [lastAt, setLastAt] = useState(() => localStorage.getItem("last_backup") || "");

  const download = async (kind) => {
    setBusy(kind);
    try {
      const { data, headers } = await api.get(`/backup/${kind}`, { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `backup-tacilaine.${kind === "json" ? "json" : "xlsx"}`;
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      const now = new Date().toISOString();
      localStorage.setItem("last_backup", now);
      setLastAt(now);
      toast.success("Backup baixado! Guarde o arquivo em local seguro. 🔒");
    } catch {
      toast.error("Não consegui gerar o backup agora. Tente novamente.");
    } finally { setBusy(""); }
  };

  const dias = daysSince(lastAt);
  const needsBackup = dias === null || dias >= 7;

  return (
    <div data-testid="backup-page" className="max-w-2xl">
      <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Segurança dos dados</p>
      <h1 className={h1}>Backup</h1>
      <p className="font-body text-sm text-[#7A8A7A] mt-2">
        Baixe uma cópia de todos os seus dados: pacientes, agenda, pagamentos, evoluções, dados clínicos e mensagens.
      </p>

      <div
        className={`mt-6 rounded-2xl p-4 flex items-start gap-3 ${needsBackup ? "bg-[#C87A5E]/10 border border-[#C87A5E]/30" : "bg-[#5B6E5B]/10 border border-[#5B6E5B]/25"}`}
        data-testid="backup-reminder"
      >
        <Clock className={`w-5 h-5 mt-0.5 ${needsBackup ? "text-[#C87A5E]" : "text-[#5B6E5B]"}`} />
        <div className="font-body text-sm text-[#2C332A]">
          {lastAt ? (
            <>Último backup neste dispositivo: <strong>{fmtWhen(lastAt)}</strong>{dias !== null && dias > 0 ? ` (há ${dias} dia${dias > 1 ? "s" : ""})` : " (hoje)"}.</>
          ) : (
            <>Você ainda não baixou nenhum backup neste dispositivo.</>
          )}
          {needsBackup && <div className="mt-1 text-[#B2644D]">Recomendamos fazer backup pelo menos <strong>uma vez por semana</strong>.</div>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div className={`${card} p-6 flex flex-col`} data-testid="backup-json-card">
          <FileJson className="w-8 h-8 text-[#5B6E5B]" />
          <p className="font-display text-xl text-[#2C332A] mt-3">Backup completo (JSON)</p>
          <p className="font-body text-sm text-[#7A8A7A] mt-1 flex-1">
            Arquivo único com todos os dados. Ideal para guardar como cópia de segurança e restaurar se precisar.
          </p>
          <button onClick={() => download("json")} disabled={!!busy} className={`${btnPrimary} w-full mt-4`} data-testid="download-json">
            {busy === "json" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Baixar JSON
          </button>
        </div>

        <div className={`${card} p-6 flex flex-col`} data-testid="backup-excel-card">
          <FileSpreadsheet className="w-8 h-8 text-[#C87A5E]" />
          <p className="font-display text-xl text-[#2C332A] mt-3">Planilha (Excel)</p>
          <p className="font-body text-sm text-[#7A8A7A] mt-1 flex-1">
            Fácil de abrir e ler, com abas: Pacientes, Agenda, Evoluções, Dados clínicos e Mensagens.
          </p>
          <button onClick={() => download("excel")} disabled={!!busy}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#C87A5E] px-6 py-3 text-sm text-[#FDFBF7] hover:bg-[#B2644D] transition-colors disabled:opacity-60" data-testid="download-excel">
            {busy === "excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Baixar Excel
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-white/60 border border-[#5B6E5B]/15 p-4 flex items-start gap-3" data-testid="backup-privacy-note">
        <ShieldAlert className="w-5 h-5 text-[#5B6E5B] mt-0.5" />
        <p className="font-body text-xs text-[#7A8A7A] leading-relaxed">
          Os arquivos contêm dados sigilosos de pacientes. Guarde-os em local seguro (nuvem privada, pendrive ou HD com senha)
          e evite enviar por canais não protegidos. Este backup é uma cópia local sua e não substitui as obrigações de sigilo (LGPD/CFP).
        </p>
      </div>
    </div>
  );
}
